import AuthModal from 'components/AuthModal/AuthModal';
import { useAnalytics } from 'contexts/Analytics/Analytics';
import { resolveAuthApiBaseUrl } from 'helpers';
import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';

const ORCA_PATH_MAP: Record<string, string> = {
    'auth/user': 'user/info/',
    'auth/recovery-send': 'auth/restore_password/send',
    'auth/recovery-verify': 'auth/restore_password/verify',
};

function resolveOrcaPath(path: string): string {
    if (ORCA_PATH_MAP[path]) {
        return ORCA_PATH_MAP[path];
    }
    // kromatic/game-progress/{slug} → kromatic/user/game-progress/{slug}
    if (path.startsWith('kromatic/game-progress/')) {
        return path.replace('kromatic/game-progress/', 'kromatic/user/game-progress/');
    }
    return path;
}

const userAccessTokenKey = 'kromatic-user-t';
const foodTruckGameSlug = 'food-truck';
const defaultLesson = '1.1';

const ApiContext = createContext<ApiState>({} as ApiState);

function normalizeAuthToken(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }

    const normalized = value.replace(/^bearer\s+/i, '').trim();
    return normalized || null;
}

function persistAuthToken(value: string | null): void {
    if (typeof window === 'undefined') {
        return;
    }

    if (value) {
        window.localStorage.setItem(userAccessTokenKey, value);
        return;
    }

    window.localStorage.removeItem(userAccessTokenKey);
}

export const useApi = () => useContext(ApiContext);

export function withApi(WrappedComponent: any) {
    return function ApiWrappedComponent(props: any) {
        return <ApiContext.Consumer>{state => <WrappedComponent {...props} api={state} />}</ApiContext.Consumer>;
    };
}

export function ApiProvider({ children }: ApiProps) {
    const [authModal, setAuthModal] = useState<AuthModalScreen | null>(null);
    const [bootstrapped, setBootstrapped] = useState(false);
    const [loadingAuthState, setLoadingAuthState] = useState(true);
    const [token, setToken] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState<ApiUserInfo | null>(null);
    const [gameProgress, setGameProgress] = useState<Record<string, ApiGameProgress | undefined>>({});
    const { trackActionEvent, trackLeadEvent, identify, clearIdentity } = useAnalytics();
    const router = useRouter();

    const authEnabled =
        !!process.env.AUTH_API ||
        (typeof window !== 'undefined' &&
            !!resolveAuthApiBaseUrl(process.env.NEXT_PUBLIC_AUTH_API_URL, {
                host: window.location.hostname,
                origin: window.location.origin,
            }));

    const clearAuth = useCallback(() => {
        persistAuthToken(null);
        if (typeof window !== 'undefined') {
            // Drop the persisted auid so a stale identity can't outlive the
            // session via the pre-bootstrap re-push (RSB#735).
            window.localStorage.removeItem('kromatic-user-auid');
        }
        setToken(null);
        setUserInfo(null);
        setGameProgress({});
        // Clear the GA4 user_id so a stale identity can't outlive the session (#481).
        clearIdentity();
    }, [clearIdentity]);

    const request = useCallback(
        async (method: string, path: string, query?: ApiRequestParams | null, data?: ApiRequestParams | null) => {
            const options: any = {
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                },
                method,
            };

            if (data) {
                options.body = JSON.stringify(data);
            }

            if (options.headers && token) {
                options.headers.Authorization = `Bearer ${token}`;
            }

            const baseUrl = resolveAuthApiBaseUrl(process.env.NEXT_PUBLIC_AUTH_API_URL, {
                host: typeof window !== 'undefined' ? window.location.hostname : undefined,
                origin: typeof window !== 'undefined' ? window.location.origin : undefined,
            });
            const orcaPath = resolveOrcaPath(path);
            let url = `${baseUrl}/${orcaPath}`;
            if (query) {
                url += `?${new URLSearchParams(query)}`;
            }

            return fetch(url, options).then(response => response.json() as ApiResponse);
        },
        [token]
    );

    const fetchGameProgress = useCallback(
        async (gameSlug: string): Promise<ApiGameProgress | null> => {
            const response = await request('GET', `kromatic/game-progress/${encodeURIComponent(gameSlug)}`);
            if (response.errors) {
                return null;
            }

            const progress = response as ApiGameProgress;
            setGameProgress(prev => ({
                ...prev,
                [gameSlug]: progress,
            }));
            return progress;
        },
        [request]
    );

    const upsertGameProgress = useCallback(
        async (gameSlug: string, data: ApiGameProgressUpsertData): Promise<ApiGameProgress | null> => {
            const response = await request('PUT', `kromatic/game-progress/${encodeURIComponent(gameSlug)}`, null, data);
            if (response.errors) {
                return null;
            }

            const progress = response as ApiGameProgress;
            setGameProgress(prev => ({
                ...prev,
                [gameSlug]: progress,
            }));
            return progress;
        },
        [request]
    );

    const authenticate = useCallback(
        async (path: string, data: ApiLoginData | ApiRegistrationData) => {
            const isRegistration = path === 'auth/registration';
            if (isRegistration) {
                void trackActionEvent('action_signup_started', {
                    auth_state: 'guest',
                    resource_type: 'tool',
                    source: 'auth_modal_registration',
                });
            }

            const response = await request('POST', path, null, data);
            if (response.errors) {
                if (isRegistration) {
                    void trackActionEvent('action_signup_failed', {
                        auth_state: 'guest',
                        resource_type: 'tool',
                        source: 'auth_modal_registration',
                    });
                }

                return response;
            }

            const nextToken = normalizeAuthToken(response.access_token || null);
            persistAuthToken(nextToken);
            setAuthModal(null);
            setToken(nextToken);
            if (!nextToken) {
                setUserInfo(null);
                setGameProgress({});
            }

            if (isRegistration) {
                void trackActionEvent('action_signup_completed', {
                    auth_state: 'member',
                    resource_type: 'tool',
                    source: 'auth_modal_registration',
                });
                void trackLeadEvent('lead_soft_signup_completed', {
                    auth_state: 'member',
                    resource_type: 'tool',
                    source: 'auth_modal_registration',
                });
            }

            return response;
        },
        [request, trackActionEvent, trackLeadEvent]
    );

    const login = useCallback((data: ApiLoginData) => authenticate('auth/login', data), [authenticate]);

    const logout = useCallback(async () => {
        try {
            if (token) {
                await request('POST', 'auth/logout');
            }
        } finally {
            clearAuth();
        }
    }, [clearAuth, request, token]);

    const registration = useCallback(
        (data: ApiRegistrationData) => authenticate('auth/registration', data),
        [authenticate]
    );

    const getResumeLesson = useCallback(
        (fallback = defaultLesson) => {
            const progress = gameProgress[foodTruckGameSlug];
            const checkpoint = progress?.last_checkpoint_id || progress?.highest_checkpoint_id;
            if (checkpoint && setup.lessonKeys.includes(checkpoint)) {
                return checkpoint;
            }
            return fallback;
        },
        [gameProgress]
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const storedToken = window.localStorage.getItem(userAccessTokenKey);
        const normalizedToken = normalizeAuthToken(storedToken);
        if (storedToken !== normalizedToken) {
            persistAuthToken(normalizedToken);
        }

        setToken(normalizedToken);
        setBootstrapped(true);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const handleStorage = (event: StorageEvent) => {
            if (event.key !== userAccessTokenKey) {
                return;
            }

            const normalizedToken = normalizeAuthToken(event.newValue);
            if (event.newValue !== normalizedToken) {
                persistAuthToken(normalizedToken);
            }

            setToken(normalizedToken);
            if (!normalizedToken) {
                setUserInfo(null);
                setGameProgress({});
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    useEffect(() => {
        if (!bootstrapped) {
            return;
        }

        if (!authEnabled) {
            setLoadingAuthState(false);
            return;
        }

        if (!token) {
            setUserInfo(null);
            setGameProgress({});
            setLoadingAuthState(false);
            return;
        }

        let cancelled = false;
        setLoadingAuthState(true);

        request('GET', 'auth/user')
            .then(data => {
                if (cancelled) {
                    return;
                }

                if (data.errors) {
                    clearAuth();
                    setLoadingAuthState(false);
                    return;
                }

                setUserInfo(data as ApiUserInfo);

                // Push the salted-HMAC auid pseudonym as the GA4 user_id (#481).
                // Never the raw id/email/name. PII HARD RULE: reject blank and
                // all-digit values so a raw numeric id can never leak as user_id.
                const rawAuid = (data as { auid?: unknown })?.auid;
                const auid =
                    typeof rawAuid === 'string' && rawAuid.trim() && !/^\d+$/.test(rawAuid.trim()) ? rawAuid : null;
                if (auid) {
                    identify(auid);
                    // Persist the auid so the Analytics provider can re-push
                    // set_user_id BEFORE GTM bootstrap on the next authed load
                    // (RSB#735). Salted-HMAC pseudonym only — never raw id/email/name.
                    if (typeof window !== 'undefined') {
                        window.localStorage.setItem('kromatic-user-auid', auid);
                    }
                } else {
                    // A successful /user/info/ with no auid means this session has
                    // no analytics identity. Clear any auid persisted by a prior
                    // user/session so the pre-bootstrap re-push can't resurrect a
                    // stale identity on the next load (RSB#735).
                    if (typeof window !== 'undefined') {
                        window.localStorage.removeItem('kromatic-user-auid');
                    }
                    clearIdentity();
                }

                void fetchGameProgress(foodTruckGameSlug).finally(() => {
                    if (!cancelled) {
                        setLoadingAuthState(false);
                    }
                });
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadingAuthState(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authEnabled, bootstrapped, clearAuth, clearIdentity, fetchGameProgress, identify, request, token]);

    const ready = useMemo(() => {
        if (!bootstrapped) {
            return false;
        }

        if (!authEnabled) {
            return true;
        }

        const route = router.asPath.split(/[?#]/)[0];
        if (!route.startsWith('/lessons')) {
            return true;
        }

        if (loadingAuthState) {
            return false;
        }

        return true;
    }, [authEnabled, bootstrapped, loadingAuthState, router.asPath]);

    // Anonymous visitors must never see a playable lesson (#171) — once auth
    // state resolves, any unauthenticated /lessons/* deep-link bounces to the
    // landing, matching plinkromatic. `ready` already withholds rendering
    // until loadingAuthState has settled for lesson routes, so this only
    // trips once we actually know the visitor has no session.
    const isAnonymousLessonPath = useMemo(() => {
        if (!authEnabled || token) {
            return false;
        }
        return router.asPath.split(/[?#]/)[0].startsWith('/lessons');
    }, [authEnabled, router.asPath, token]);

    useEffect(() => {
        if (ready && isAnonymousLessonPath) {
            router.replace('/');
        }
    }, [isAnonymousLessonPath, ready, router]);

    const state = useMemo<ApiState>(
        () => ({
            authenticate: authEnabled,
            authenticated: !!token,
            loadingAuthState,
            userInfo,
            gameProgress,
            delete: (path, query) => request('DELETE', path, query),
            get: (path, query) => request('GET', path, query),
            login,
            logout,
            post: (path, query, data) => request('POST', path, query, data),
            put: (path, query, data) => request('PUT', path, query, data),
            registration,
            showAuthModal: screen => setAuthModal(screen || 'login'),
            getResumeLesson,
            refreshGameProgress: fetchGameProgress,
            upsertGameProgress,
        }),
        [
            authEnabled,
            token,
            loadingAuthState,
            userInfo,
            gameProgress,
            request,
            login,
            logout,
            registration,
            getResumeLesson,
            fetchGameProgress,
            upsertGameProgress,
        ]
    );

    return (
        <ApiContext.Provider value={state}>
            {authModal && <AuthModal screen={authModal} onClose={() => setAuthModal(null)} />}
            {ready && !isAnonymousLessonPath && children}
        </ApiContext.Provider>
    );
}
